'use client'

import type { Media, Partner } from '@/payload-types'
import { getAssetUrl } from '@/website/utils/getAssetUrl'

type Props = {
    colors?: Partner['colors'] | null;
    backgroundImage?: Media | string | null;
    buttonBorder?: Partner['buttonBorder'] | null;
    gameTile?: Partner['gameTile'] | null;
};

export const ClientPartnerStyling = (props: Props) => {
    const { backgroundImage, buttonBorder, colors, gameTile } = props

    const {
        pageBackground,
        pageText,
        buttonPrimaryBackground,
        buttonPrimaryText,
        buttonSecondaryBackground,
        buttonSecondaryText,
        gameTileBackground,
        gameTileText,
        gameTileBackgroundHover,
        gameTileTextHover,
        gameTileTagBackground,
        gameTileTagBackgroundTwo,
        gameTileTagText,
        gameLowerBackground,
        gameLowerText,
        navBarBackground,
        navBarText,
        footerBackground,
        footerText,
     } = colors ?? {}

    const {
        radius,
        style,
        borderColour,
        borderColourActive,
        width,
    } = buttonBorder ?? {}

    const {
        dropShadow,
        dropShadowHovered,
        borderColour: gameTileBorderColour,
        style: gameTileBorderStyle,
        radius: gameTileBorderRadius,
        width: gameTileBorderWidth,
        scaleOnHover,
    } = gameTile ?? {}

    let gameTileTagBg;
    if(gameTileTagBackground) {
        gameTileTagBg = `linear-gradient(270deg, ${gameTileTagBackground} 0%, ${gameTileTagBackgroundTwo ?? gameTileTagBackground} 100%)`
    }

    return (
        <style type="text/css">
            {`
                :root {
                    ${backgroundImage ? `--page-background-image: url('${getAssetUrl((backgroundImage as Media).url!)}');` : ''}
                    ${pageBackground ? `--page-background: ${pageBackground};` : ''}
                    ${pageText ? `--page-text: ${pageText};` : ''}
                    ${buttonPrimaryBackground ? `--button-primary-background: ${buttonPrimaryBackground};` : ''}
                    ${buttonPrimaryText ? `--button-primary-text: ${buttonPrimaryText};` : ''}
                    ${buttonSecondaryBackground ? `--button-secondary-background: ${buttonSecondaryBackground};` : ''}
                    ${buttonSecondaryText ? `--button-secondary-text: ${buttonSecondaryText};` : ''}
                    ${gameTileBackground ? `--game-tile-background: ${gameTileBackground};` : ''}
                    ${gameTileText ? `--game-tile-text: ${gameTileText};` : ''}
                    ${gameTileBackgroundHover ? `--game-tile-background-hovered: ${gameTileBackgroundHover};` : ''}
                    ${gameTileTextHover ? `--game-tile-text-hovered: ${gameTileTextHover};` : ''}
                    ${(scaleOnHover ?? 108) ? `--game-tile-scale-hovered: ${scaleOnHover}%;` : ''}
                    ${gameTileBorderColour ? `--game-tile-border-colour: ${gameTileBorderColour};` : ''}
                    ${gameTileBorderRadius !== undefined && gameTileBorderRadius !== null ? `--game-tile-border-radius: ${gameTileBorderRadius}px;` : ''}
                    ${gameTileBorderStyle ? `--game-tile-border-style: ${gameTileBorderStyle};` : ''}
                    ${gameTileBorderWidth !== undefined && gameTileBorderWidth !== null ? `--game-tile-border-width: ${gameTileBorderWidth}px;` : ''}
                    ${gameTileBorderRadius !== undefined && gameTileBorderRadius !== null ? gameTileBorderWidth !== undefined && gameTileBorderWidth !== null ? `--game-tile-outer-border-radius: ${gameTileBorderRadius + gameTileBorderWidth}px;` : `--game-tile-outer-border-radius: ${gameTileBorderRadius}px;` : ''}
                    ${borderColour ? `--border-colour: ${borderColour};` : ''}
                    ${gameTileTagBg ? `--game-tile-tag-background: ${gameTileTagBg};` : ''}
                    ${gameTileTagText ? `--game-tile-tag-text: ${gameTileTagText};` : ''}
                    ${gameLowerBackground ? `--game-lower-background: ${gameLowerBackground};` : ''}
                    ${gameLowerText ? `--game-lower-text: ${gameLowerText};` : ''}
                    ${navBarBackground ? `--nav-bar-background: ${navBarBackground};` : ''}
                    ${navBarText ? `--nav-bar-text: ${navBarText};` : ''}
                    ${footerBackground ? `--footer-background: ${footerBackground};` : ''}
                    ${footerText ? `--footer-text: ${footerText};` : ''}
                    ${radius ? `--button-border-radius: ${radius}px;` : ''}
                    ${style ? `--button-border-style: ${style};` : ''}
                    ${width ? `--button-border-width: ${width}px;` : ''}
                    ${borderColour ? `--button-border-colour: ${borderColour};` : ''}
                    ${borderColourActive ? `--button-border-colour-active: ${borderColourActive};` : ''}
                    ${
                        dropShadow ? {
                            'none': `--box-shadow: 0 0 #0000;`,
                            'sm': `--box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);`,
                            'md': `--box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);`,
                            'lg': `--box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);`,
                        }[dropShadow] : ''
                    }
                    ${
                        dropShadowHovered ? {
                            'none': `--box-shadow-hover: 0 0 #0000;`,
                            'sm': `--box-shadow-hover: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);`,
                            'md': `--box-shadow-hover: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);`,
                            'lg': `--box-shadow-hover: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);`,
                            'xl': `--box-shadow-hover: 0 15px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.12);`
                        }[dropShadowHovered] : ''
                    }
                }
            `}
        </style>
    )
}
